/*
 * assets/c-dialog.js - the modal-dialog helper the legacy dialogs share.
 *
 * A11Y-02 (#109), A11Y-09 (#116), A11Y-11 (#290), A11Y-20 (#302). The cart drawer, the mobile menu,
 * the country and currency modal and the breed modal used to toggle a class and nothing else: focus
 * stayed on the page behind them, Tab walked into that page, and two of them ignored Escape. This
 * file is the one place that knows how a modal behaves. Each component calls it when it opens or
 * closes; assets/section-search-modal.js already behaves and does not use it.
 *
 *   window.CDialog.open({ root, keep, initialFocus, returnFocus, fallbackFocus, onEscape })
 *     root           the dialog element. Opening a root that is already open, or that has left the
 *                    document, does nothing.
 *     keep           other elements that must stay usable while it is open: the cart overlay
 *                    (clicking it closes the drawer, and an inert element cannot be clicked), the
 *                    header bar that holds the mobile-menu button.
 *     initialFocus   focused once the page behind is inert. Optional.
 *     returnFocus    where focus goes on close. Default: whatever had focus when open() ran.
 *     fallbackFocus  where focus goes on close when returnFocus is gone, hidden, inert or <body>.
 *     onEscape       run when Escape is pressed while this is the top dialog. Leave it out when the
 *                    component handles Escape itself, and have that handler call preventDefault():
 *                    an Escape already handled is skipped here, so it cannot also close a dialog
 *                    open underneath (A11Y-11 #290, A11Y-20 #302).
 *   window.CDialog.close(root, { restoreFocus = true })
 *   window.CDialog.isOpen(root)
 *
 * The page behind is made inert by walking from the root and from each kept element up to <body>,
 * setting `inert` on every sibling that is not on one of those paths (scripts, styles and anything
 * marked data-c-dialog-keep excepted). Only the nodes this file set are ever cleared, so an element
 * some other script made inert stays inert. Dialogs stack, and every open and close releases
 * everything and re-applies for the dialog now on top, so they can close in any order - or after
 * their root has left the document - and leave nothing inert behind. There is no Tab trap: with the
 * rest of the page inert, Tab cycles through the dialog and the browser's own controls, as it does
 * in a native modal <dialog>.
 *
 * A classic deferred script, loaded once from layout/theme.liquid before section-header.js. Callers
 * use window.CDialog?.open(...), so a page without this file behaves as it did before it existed.
 * Tests: tests/assets/c-dialog.test.mjs.
 */
(function () {
  "use strict";

  if (window.CDialog) return;

  const SKIP_TAGS = /^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/;
  const stack = [];
  let changed = [];

  const release = () => {
    changed.forEach((node) => {
      node.inert = false;
    });
    changed = [];
  };

  const apply = () => {
    release();
    const top = stack[stack.length - 1];
    if (!top) return;

    const path = [];
    top.keep.forEach((element) => {
      for (let node = element; node && node.parentElement; node = node.parentElement) {
        if (path.indexOf(node) === -1) path.push(node);
      }
    });

    path.forEach((node) => {
      if (top.keep.indexOf(node) !== -1) return;
      Array.prototype.forEach.call(node.children, (child) => {
        if (path.indexOf(child) !== -1) return;
        if (child.inert) return;
        if (SKIP_TAGS.test(child.tagName)) return;
        if (child.hasAttribute("data-c-dialog-keep")) return;
        child.inert = true;
        changed.push(child);
      });
    });
  };

  const insideInert = (element) => {
    for (let node = element; node; node = node.parentElement) {
      if (node.inert) return true;
    }
    return false;
  };

  const canTakeFocus = (element) =>
    Boolean(element) &&
    typeof element.focus === "function" &&
    element !== document.body &&
    element.isConnected !== false &&
    !insideInert(element) &&
    (typeof element.getClientRects !== "function" || element.getClientRects().length > 0);

  const moveFocus = (element) => {
    if (element && typeof element.focus === "function") element.focus({ preventScroll: true });
  };

  const onKeydown = (event) => {
    if (event.key !== "Escape" || event.defaultPrevented) return;
    const top = stack[stack.length - 1];
    if (!top || typeof top.onEscape !== "function") return;
    event.preventDefault();
    top.onEscape();
  };

  const indexOf = (root) => stack.findIndex((entry) => entry.root === root);

  const open = (options) => {
    const root = options && options.root;
    if (!root || indexOf(root) !== -1) return;
    /* A11Y-02 (#109), global constraint: never open a root that has left the document (a stale
       instance a Theme Editor reload left subscribed); it would inert the live page and never be
       closed. Strictly false: a root that does not report isConnected is taken as connected. */
    if (root.isConnected === false) return;

    stack.push({
      root,
      keep: [root].concat((options.keep || []).filter(Boolean)),
      returnFocus: options.returnFocus || document.activeElement,
      fallbackFocus: options.fallbackFocus || null,
      onEscape: options.onEscape || null
    });
    apply();
    if (stack.length === 1) document.addEventListener("keydown", onKeydown);
    if (options.initialFocus) moveFocus(options.initialFocus);
  };

  const close = (root, options) => {
    const index = indexOf(root);
    if (index === -1) return;

    const entry = stack.splice(index, 1)[0];
    apply();
    if (!stack.length) document.removeEventListener("keydown", onKeydown);
    if (options && options.restoreFocus === false) return;

    if (canTakeFocus(entry.returnFocus)) moveFocus(entry.returnFocus);
    else if (canTakeFocus(entry.fallbackFocus)) moveFocus(entry.fallbackFocus);
  };

  window.CDialog = {
    open,
    close,
    isOpen: (root) => indexOf(root) !== -1
  };
})();
